import React, { useState } from "react"
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material"
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { downloadDocument } from "../utils/downloadDocuments"
import FirebaseService from "../services/firebase"
import Swal from "sweetalert2"

export default function Multifile({ files = [], setFiles, firebaseFolder, loading, setLoading,
  readOnly = false, detachOnly = false }) {
  const [error, setError] = useState("")
  const upload = async (selected) => {
    if (!selected.length || readOnly || loading) return
    setLoading(true)
    setError("")
    const failed = []
    try {
      for (const file of selected) {
        try {
          const nombreFirebase = `${Date.now()}_${Math.random().toString(36).slice(2)}_${file.name}`
          const url = await FirebaseService.uploadOrThrow(`${firebaseFolder}/${nombreFirebase}`, file)
          setFiles(previous => [...(previous || []), { nombre: file.name,
            tipo: file.type || "application/octet-stream", url, nombreFirebase,
            firebaseName: nombreFirebase, folder: firebaseFolder }])
        } catch (_) { failed.push(file.name) }
      }
      if (failed.length) setError(`No se pudieron subir: ${failed.join(", ")}. Revisa la conexión con el almacenamiento y vuelve a intentarlo.`)
    } finally { setLoading(false) }
  }
  const remove = async file => {
    const result = await Swal.fire({ title: `¿Quitar ${file.nombre}?`, icon: "warning",
      showCancelButton: true, confirmButtonText: "Quitar", cancelButtonText: "Cancelar" })
    if (!result.isConfirmed) return
    setLoading(true)
    setError("")
    try {
      // El vínculo del carro se guarda en la transacción; no borrar el objeto antes.
      if (!detachOnly) await FirebaseService.deleteFile(`${file.folder || firebaseFolder}/`, file.nombreFirebase || file.firebaseName)
      setFiles(previous => previous.filter(item => item !== file))
    } catch (_) { setError("No se pudo quitar el archivo. Intenta nuevamente.") }
    finally { setLoading(false) }
  }
  const download = async file => {
    setError("")
    try {
      await downloadDocument(file)
    } catch (_) { setError("No se pudo descargar el archivo. Puedes intentar abrirlo o revisar la conexión.") }
  }
  return <Stack spacing={2.5} sx={{ width: "100%", minWidth: 0, "& .MuiButton-root": { textTransform: "none", borderRadius: 2, boxShadow: "none" } }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ sm: "center" }}>
      <Box><Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 650, color: "#23374b" }}>Documentos adjuntos</Typography>
        <Chip size="small" label={files?.length || 0} sx={{ bgcolor: "#edf3f8", color: "#526980" }} />
      </Stack><Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Pólizas, tarjetas de circulación, fichas técnicas y bitácoras.</Typography></Box>
      {!readOnly && <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={loading} sx={{ flexShrink: 0, px: 2.5, py: 1 }}>Subir archivos
        <input hidden type="file" multiple onChange={event => {
          const selected = Array.from(event.target.files || [])
          event.target.value = ""
          void upload(selected)
        }} />
      </Button>}
    </Stack>
    {error && <Alert severity="error">{error}</Alert>}
    {loading && <CircularProgress size={24} />}
    {!files?.length && <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", border: "1px dashed #cdd9e5", borderRadius: 2 }}>
      <UploadFileOutlinedIcon sx={{ fontSize: 34, color: "#8b9db0", mb: 1 }} />
      <Typography color="text.secondary">No hay documentos adjuntos.</Typography>
      {!readOnly && <Typography variant="caption" color="text.secondary">Agrega PDF, Excel, imágenes u otros archivos de la unidad.</Typography>}
    </Box>}
    <Stack spacing={1.25}>{(files || []).map((file, index) => {
      const extension = file.nombre?.split(".").pop()?.toUpperCase() || "ARCHIVO"
      const color = extension === "PDF" ? "#c94545" : ["XLS", "XLSX", "CSV"].includes(extension) ? "#27865b" : "#157cc1"
      return <Paper key={file.idArchivo || file.nombreFirebase || file.firebaseName || index} variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: "#e4eaf0", background: "#fbfcfe", boxShadow: "none" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Stack direction="row" spacing={1.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ p: 1.25, display: "flex", borderRadius: 2, color, bgcolor: `${color}10` }}><InsertDriveFileOutlinedIcon /></Box>
            <Box sx={{ minWidth: 0 }}><Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere", color: "#23374b" }}>{file.nombre}</Typography>
              <Typography variant="caption" color="text.secondary">{extension} · {file.idArchivo ? "Guardado" : "Pendiente de guardar"}</Typography></Box>
          </Stack>
          <Stack direction="row" spacing={.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title="Abrir archivo"><IconButton aria-label={`Abrir ${file.nombre}`} component="a" href={file.url} target="_blank" rel="noopener noreferrer" size="small" sx={{ color: "#657b91" }}><OpenInNewOutlinedIcon fontSize="small" /></IconButton></Tooltip>
            <Button startIcon={<FileDownloadOutlinedIcon />} size="small" onClick={() => download(file)}>Descargar</Button>
            {!readOnly && <Tooltip title="Quitar archivo"><span><IconButton aria-label={`Quitar ${file.nombre}`} color="error" disabled={loading} size="small" onClick={() => remove(file)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>}
          </Stack>
        </Stack>
      </Paper>
    })}</Stack>
    {!readOnly && !!files?.length && <Typography variant="caption" color="text.secondary">Los archivos nuevos y los cambios se conservarán al guardar.</Typography>}
  </Stack>
}
